import React, { MouseEvent as ReactMouseEvent } from 'react';

import { styled } from '@mui/material';

import ArrowRightIcon from '~/assets/icons/ArrowRightIcon';
import PlaySmallIcon from '~/assets/icons/PlaySmallIcon';
import ReplayIcon from '~/assets/icons/ReplayIcon';
import CustomButton, { BUTTON_COLOR } from '~/components/UI/CustomButton/CustomButton';
import BannerWithButton from '~/components/VideoPlayer/BannerWithButton';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import {
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '~/theme';
import { CaseStudy, Quiz, Video } from '~/types';
import { secondsToHHHmmm } from '~/utils/dateUtils/secondsToHHHmmm';

interface IVideoNavigation {
  showBanner?: boolean;
  replayHandler: (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
  nextItem?: Quiz | Video | CaseStudy | null;
  onContinueClick?: () => void;
}

const VideoPlayerNavigation = ({
  showBanner,
  replayHandler,
  nextItem,
  onContinueClick,
}: IVideoNavigation) => {
  const { t } = useTenantTranslation();
  const isMobile = !useIsWidthUp('md');

  const replayButton = isMobile ? (
    <ReplayWrapper>
      <StyledReplayIcon onClick={replayHandler} />
    </ReplayWrapper>
  ) : (
    <ReplayButtonWrapper>
      <CustomButton
        data-testid="replayButton"
        color={BUTTON_COLOR.Secondary}
        onClick={replayHandler}
      >
        {t('button.replay')}
      </CustomButton>
    </ReplayButtonWrapper>
  );

  return (
    <Wrapper data-testid="nextItemBanner">
      {replayButton}
      {showBanner && nextItem?.__typename === 'Video' && (
        <BannerWithButton
          caption={t('page.video.nextVideo') + ':'}
          title={nextItem?.title}
          subtitle={secondsToHHHmmm(nextItem?.duration)}
          buttonCaption={t('page.video.continueLearning')}
          onClick={onContinueClick}
          mobileIcon={<PlayIcon />}
        />
      )}
      {showBanner && nextItem?.__typename === 'CaseStudy' && (
        <BannerWithButton
          caption={t('page.caseStudy.title') + ':'}
          title={nextItem?.title}
          subtitle={t('common.questionsAmount', {
            count: nextItem?.questions?.length,
          })}
          buttonCaption={t('page.pathway.startCaseStudy')}
          onClick={onContinueClick}
          mobileIcon={<PlayIcon />}
        />
      )}
      {showBanner && nextItem?.__typename === 'Quiz' && (
        <BannerWithButton
          caption={t('page.video.test') + ':'}
          title={nextItem?.title}
          subtitle={secondsToHHHmmm(nextItem?.time_for_pass ? nextItem?.time_for_pass * 60 : null)}
          buttonCaption={t('page.video.takeTest')}
          onClick={onContinueClick}
          mobileIcon={<StyledArrowRight />}
        />
      )}
    </Wrapper>
  );
};

export default VideoPlayerNavigation;

const Wrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-wrap: nowrap;
    padding: ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} 0;
  }
`;
const ReplayWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  min-width: 54px;
  height: 54px;
  margin: 0 ${SUBMIDDLE_MARGIN_PX} ${TINY_MARGIN_PX} 0;
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.palette.common.button.collapseSecondary};
  background: ${({ theme }) => theme.palette.common.button.replayButton};
`;
const PlayIcon = styled(PlaySmallIcon)`
  position: relative;
  right: -1px;
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.palette.common.text.white};
`;
const StyledArrowRight = styled(ArrowRightIcon)`
  width: 18px;
  height: 18px;
`;
const ReplayButtonWrapper = styled('div')`
  margin-right: ${MIDDLE_MARGIN_PX};
`;
const StyledReplayIcon = styled(ReplayIcon)`
  color: ${({ theme }) => theme.palette.common.text.white};
`;
